import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  AuthContext,
  useAuth,
  useAuthProvider,
} from "./hooks/useAuth";
import { Layout } from "./components/Layout";
import { Home } from "./pages/Home";
import { Landing } from "./pages/Landing";
import { Memory } from "./pages/Memory";
import { Policy } from "./pages/Policy";
import { Settings } from "./pages/Settings";
import { Chat } from "./pages/Chat";
import { Artifacts } from "./pages/Artifacts";
import { Setup } from "./pages/Setup";
import { VoiceJobs } from "./pages/VoiceJobs";
import { CallList } from "./pages/CallList";
import { ComplianceDashboard } from "./pages/ComplianceDashboard";
import { CallDetail } from "./pages/CallDetail";
import { VoiceTest } from "./pages/VoiceTest";
import { Login } from "./pages/Login";
import { Signup } from "./pages/Signup";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function PageLoading() {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-bg">
      <div className="stage" />
      <div className="relative">
        <div className="mono-caps text-t3">Loading…</div>
      </div>
    </div>
  );
}

/** Gate the /app/* routes — if not authed, redirect to /login. */
function RequireAuth() {
  const { authenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoading />;
  if (!authenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <Outlet />;
}

/** Already-authed users on /login or /signup should bounce into the app. */
function PublicOnly({ children }: { children: React.ReactNode }) {
  const { authenticated, loading } = useAuth();
  if (loading) return <PageLoading />;
  if (authenticated) return <Navigate to="/app/home" replace />;
  return <>{children}</>;
}

function AppContent() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public marketing landing */}
        <Route path="/" element={<Landing />} />

        {/* Auth pages */}
        <Route
          path="/login"
          element={
            <PublicOnly>
              <Login />
            </PublicOnly>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicOnly>
              <Signup />
            </PublicOnly>
          }
        />

        {/* Gated app */}
        <Route element={<RequireAuth />}>
          <Route path="/app" element={<Layout />}>
            <Route index element={<Navigate to="home" replace />} />
            <Route path="home" element={<Home />} />

            {/* Calls */}
            <Route path="jobs" element={<VoiceJobs />} />
            <Route path="calls" element={<CallList />} />
            <Route path="calls/test" element={<VoiceTest />} />
            <Route path="calls/:id" element={<CallDetail />} />

            {/* Chat */}
            <Route path="chat" element={<Chat />} />

            {/* Memory — Contacts / Facts / Audit / Artifacts */}
            <Route path="memory/*" element={<Memory />} />

            {/* Policy — rules / DNC / review queue */}
            <Route path="policy/*" element={<Policy />} />

            {/* Settings — Connections / Voice / Compliance / Account */}
            <Route path="settings/*" element={<Settings />} />
          </Route>
        </Route>

        {/* Anything unknown → root */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </QueryClientProvider>
  );
}

function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuthProvider();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}
